import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { EquipmentCategory, FieldCapability, FieldLaborRoleOption, FieldProficiencyLevel } from './field-work.models';

export type FieldWorkSetupDialogType = 'role' | 'capability' | 'proficiency' | 'equipment-category';
export type FieldWorkSetupRecord = FieldLaborRoleOption | FieldCapability | FieldProficiencyLevel | EquipmentCategory;

@Component({
  selector: 'app-field-work-setup-create-dialog',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonModule, DialogModule, IftaLabelModule, InputGroupModule,
    InputGroupAddonModule, InputNumberModule, InputTextModule, MultiSelectModule, SelectModule
  ],
  templateUrl: './field-work-setup-create-dialog.component.html',
  styleUrl: './field-work-setup-create-dialog.component.css'
})
export class FieldWorkSetupCreateDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() type: FieldWorkSetupDialogType = 'role';
  @Input() saving = false;
  @Input() error = '';
  @Input() capabilities: FieldCapability[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() create = new EventEmitter<FieldWorkSetupRecord>();

  form: FieldWorkSetupRecord = this.emptyForm();
  roleCapabilityIds: number[] = [];
  validationError = '';

  get title(): string {
    return {
      role: 'Add Operational Role',
      capability: 'Add Capability',
      proficiency: 'Add Proficiency Level',
      'equipment-category': 'Add Equipment Category'
    }[this.type];
  }

  get summaryTitle(): string {
    return {
      role: 'Create an operational role',
      capability: 'Create an eligibility capability',
      proficiency: 'Create a proficiency level',
      'equipment-category': 'Create an equipment category'
    }[this.type];
  }

  get summaryCopy(): string {
    return {
      role: 'Roles define the operational function required in a Field Work package.',
      capability: 'Capabilities qualify who can fulfil a role and can be assigned to Field People.',
      proficiency: 'Ordered ranks establish the minimum level required for a capability.',
      'equipment-category': 'Categories define reusable equipment availability and tracking rules.'
    }[this.type];
  }

  get icon(): string {
    return {
      role: 'pi pi-briefcase', capability: 'pi pi-award', proficiency: 'pi pi-chart-line',
      'equipment-category': 'pi pi-wrench'
    }[this.type];
  }

  get submitLabel(): string {
    return {
      role: 'Add role', capability: 'Add capability', proficiency: 'Add level',
      'equipment-category': 'Add category'
    }[this.type];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true || changes['type']) this.reset();
  }

  close(): void {
    if (!this.saving) this.visibleChange.emit(false);
  }

  reset(): void {
    this.validationError = '';
    this.form = this.emptyForm();
    this.roleCapabilityIds = [];
  }

  submit(): void {
    this.validationError = '';
    if (!this.isValid()) return;
    const value = this.type === 'role'
      ? { ...this.roleForm, mandatoryCapabilities: this.roleCapabilityIds.map(fieldCapabilityId => ({
        fieldCapabilityId, requirementType: 'MANDATORY'
      })) }
      : { ...this.form };
    this.create.emit(value);
  }

  private isValid(): boolean {
    const code = this.type === 'role' ? this.roleForm.roleCode
      : this.type === 'capability' ? this.capabilityForm.capabilityCode
      : this.type === 'proficiency' ? this.proficiencyForm.levelCode : this.categoryForm.categoryCode;
    const name = this.type === 'role' ? this.roleForm.roleName
      : this.type === 'capability' ? this.capabilityForm.capabilityName
      : this.type === 'proficiency' ? this.proficiencyForm.levelName : this.categoryForm.categoryName;
    if (!code?.trim() || !name?.trim()) {
      this.validationError = 'Code and name are required.';
      return false;
    }
    return true;
  }

  get roleForm(): FieldLaborRoleOption { return this.form as FieldLaborRoleOption; }
  get capabilityForm(): FieldCapability { return this.form as FieldCapability; }
  get proficiencyForm(): FieldProficiencyLevel { return this.form as FieldProficiencyLevel; }
  get categoryForm(): EquipmentCategory { return this.form as EquipmentCategory; }

  private emptyForm(): FieldWorkSetupRecord {
    switch (this.type) {
      case 'capability': return { capabilityCode: '', capabilityName: '', active: true };
      case 'proficiency': return { levelCode: '', levelName: '', levelRank: 0, active: true };
      case 'equipment-category': return {
        categoryCode: '', categoryName: '', trackingMode: 'SERIALIZED', defaultUnit: 'UNIT', active: true
      };
      default: return { roleCode: '', roleName: '', active: true };
    }
  }
}
